import { useEffect, useState } from 'react';
import { getApiErrorMessage, loyaltyAPI } from '@matix/api-client';
import { roundMoney } from '@matix/utils';
import { useCart, useCartTotals } from '../../store/cart';
import { useLoyalty } from '../../store/loyalty';
import { moneyPrecision } from '../../store/currency';
import { toast } from '../../store/toast';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { CheckIcon, TrashIcon } from '../../components/icons';
import { usePaymentTypesWarm } from './usePaymentTypes';

/**
 * Loyalty redemption, inside the payment modal.
 *
 * The cashier types (or maxes out) a number of POINTS; the server prices them
 * and returns what they are worth. Nothing here computes money: the browser
 * only ever displays a value the API returned, and the same value is
 * re-validated under lock when the order is charged. That is what keeps two
 * terminals from spending the same balance.
 *
 * Hidden entirely unless the tenant has loyalty on, this branch takes part, and
 * a customer with points is attached — a walk-in sale looks exactly as it did
 * before this feature existed.
 */
export function LoyaltyRedeemPanel({ disabled = false }: { disabled?: boolean }) {
  const { t: tr } = useLang();
  const config = useLoyalty((s) => s.config);
  const canRedeem = useLoyalty((s) => s.canRedeem);
  const loyaltyCustomer = useLoyalty((s) => s.customer);
  const customer = useCart((s) => s.customer);
  const redeem = useCart((s) => s.loyaltyRedeem);
  const setRedeem = useCart((s) => s.setLoyaltyRedeem);
  const t = useCartTotals();

  const [points, setPoints] = useState('');
  const [busy, setBusy] = useState(false);
  // A tenant with the legacy "Loyality" payment type (Code -2) takes points
  // as a PAYMENT, like the old till; anyone else keeps the discount.
  const asPayment = (usePaymentTypesWarm().data?.payment_types ?? []).some((pt) => pt.code === -2);

  // The base redemption may cover. As a discount: sale lines less the
  // cashier's own discount, plus delivery — pre-tax, since it reduces the
  // taxable base. As a payment: the whole invoice, tax included, as the server.
  const redeemableBase = roundMoney(
    asPayment ? t.saleGross : t.subtotal - t.discount + t.deliveryFee,
    moneyPrecision(),
  );

  // Clear a stale redemption if the basket shrinks below what was redeemed;
  // the server would reject it anyway, and failing at Confirm is worse than
  // failing here.
  useEffect(() => {
    if (redeem && redeem.value > redeemableBase + 1e-9) {
      setRedeem(null);
      toast(
        tr({
          en: 'Points removed — the order no longer covers them',
          ar: 'تمت إزالة النقاط — الطلب لم يعد يغطيها',
        }),
        'info',
      );
    }
  }, [redeem, redeemableBase, setRedeem, tr]);

  if (!canRedeem() || !customer || !loyaltyCustomer) return null;
  // Nothing to spend, and nothing to spend it on.
  if (loyaltyCustomer.balance_points <= 0 && !redeem) return null;
  if (redeemableBase <= 0 && !redeem) return null;

  async function applyPoints(requested: number) {
    if (!customer) return;
    if (!Number.isFinite(requested) || requested <= 0) {
      toast(tr({ en: 'Enter how many points to redeem', ar: 'أدخل عدد النقاط المطلوب استبدالها' }), 'warning');
      return;
    }
    setBusy(true);
    try {
      const quote = await loyaltyAPI.quote({
        customer_id: customer.id,
        requested_points: requested,
        redeemable_base: redeemableBase,
      });
      if (!quote.ok || quote.reason) {
        toast(reasonText(quote.reason, quote.max_redeemable_points), 'warning');
        return;
      }
      setRedeem({
        points: quote.points,
        value: quote.value,
        balancePoints: quote.balance_points,
        ...(asPayment ? { asPayment: true } : {}),
      });
      setPoints('');
      toast(
        tr({
          en: `${quote.points} points applied — ${fmt(quote.value)} off`,
          ar: `تم تطبيق ${quote.points} نقطة — خصم ${fmt(quote.value)}`,
        }),
        'success',
      );
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  /** Translate a server rejection code into something a cashier can act on. */
  function reasonText(reason: string | null, maxPoints: number): string {
    switch (reason) {
      case 'LOYALTY_INSUFFICIENT_POINTS':
        return tr({ en: 'Not enough points on this account', ar: 'النقاط غير كافية في هذا الحساب' });
      case 'LOYALTY_BELOW_MINIMUM':
        return tr({
          en: `At least ${config.min_redeem_points} points are needed to redeem`,
          ar: `الحد الأدنى للاستبدال ${config.min_redeem_points} نقطة`,
        });
      case 'LOYALTY_EXCEEDS_LIMIT':
        return tr({
          en: `At most ${maxPoints} points can be used on this order`,
          ar: `الحد الأقصى ${maxPoints} نقطة على هذا الطلب`,
        });
      case 'LOYALTY_NOTHING_TO_DISCOUNT':
        return tr({ en: 'Nothing left on this order to redeem against', ar: 'لا يوجد مبلغ متبقٍ للاستبدال' });
      case 'LOYALTY_BRANCH_NOT_ELIGIBLE':
        return tr({ en: 'This branch is not part of the loyalty programme', ar: 'هذا الفرع غير مشمول ببرنامج الولاء' });
      default:
        return tr({ en: 'Points cannot be redeemed on this order', ar: 'لا يمكن استبدال النقاط على هذا الطلب' });
    }
  }

  const balance = redeem ? redeem.balancePoints - redeem.points : loyaltyCustomer.balance_points;

  return (
    <div className="pay-loyalty">
      <div className="pay-section-title">
        {tr({ en: 'Loyalty points', ar: 'نقاط الولاء' })}
      </div>

      <div className="pay-loyalty-balance">
        <span>
          {tr({ en: 'Available', ar: 'المتاح' })}: <strong>{balance}</strong>{' '}
          {tr({ en: 'pts', ar: 'نقطة' })}
        </span>
        {loyaltyCustomer.expiring_points > 0 && (
          <span className="pay-loyalty-expiring">
            {tr({
              en: `${loyaltyCustomer.expiring_points} expiring soon`,
              ar: `${loyaltyCustomer.expiring_points} تنتهي قريبًا`,
            })}
          </span>
        )}
      </div>

      {redeem ? (
        <div className="pay-loyalty-applied">
          <span>
            {redeem.points} {tr({ en: 'pts', ar: 'نقطة' })} · −{fmt(redeem.value)}
          </span>
          <button
            type="button"
            className="pay-split-remove"
            onClick={() => setRedeem(null)}
            disabled={disabled || busy}
            title={tr({ en: 'Remove points', ar: 'إزالة النقاط' })}
          >
            <TrashIcon size={14} />
          </button>
        </div>
      ) : (
        <div className="pay-loyalty-row">
          <input
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            className="pay-loyalty-input"
            placeholder={tr({ en: 'Points', ar: 'النقاط' })}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void applyPoints(Number(points));
            }}
            disabled={disabled || busy}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void applyPoints(Number(points))}
            disabled={disabled || busy}
          >
            <CheckIcon size={14} />
            {tr({ en: 'Apply', ar: 'تطبيق' })}
          </button>
          <button
            type="button"
            className="btn-secondary"
            // 0 asks the server for the ceiling; we then apply exactly that.
            onClick={async () => {
              if (!customer) return;
              setBusy(true);
              try {
                const q = await loyaltyAPI.quote({
                  customer_id: customer.id,
                  requested_points: 0,
                  redeemable_base: redeemableBase,
                });
                setBusy(false);
                if (q.max_redeemable_points > 0) await applyPoints(q.max_redeemable_points);
                else toast(reasonText('LOYALTY_EXCEEDS_LIMIT', 0), 'warning');
              } catch (err) {
                setBusy(false);
                toast(getApiErrorMessage(err), 'error');
              }
            }}
            disabled={disabled || busy}
          >
            {tr({ en: 'Max', ar: 'الأقصى' })}
          </button>
        </div>
      )}
    </div>
  );
}
